import { useState, useEffect, useCallback } from 'react';
import { api, formatCurrency, MONTHS, checkBudgetAlerts } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import NumericKeypad from '../components/NumericKeypad';

const EMPTY_FORM = { type: 'expense', category_id: '', subcategory_id: '', amount: '', description: '', date: new Date().toISOString().split('T')[0] };

export default function Transactions() {
  const { user } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showKeypad, setShowKeypad] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [hiddenAmounts, setHiddenAmounts] = useState(() => {
    try { return localStorage.getItem('hideTransactions') === 'true'; }
    catch { return false; }
  });
  const currency = user?.currency || 'COP';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [txns, cats] = await Promise.all([
        api.get(`/transactions?month=${month}&year=${year}&limit=100`),
        api.get('/categories'),
      ]);
      setTransactions(txns);
      setCategories(cats);
    } catch (e) { toast.error('Error al cargar datos'); }
    setLoading(false);
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  // Get parent categories of selected type
  const parentCats = categories.filter(c => c.type === form.type && !c.parent_id);
  // Get subcategories of selected parent
  const selectedParent = categories.find(c => c.id === parseInt(form.category_id));
  const subCats = selectedParent?.subcategories || [];

  // The actual category_id to save: subcategory if selected, else parent
  const effectiveCategoryId = form.subcategory_id || form.category_id;

  const openAdd = () => { setForm(EMPTY_FORM); setEditId(null); setShowModal(true); };
  const openEdit = (t) => {
    // Find if transaction category is a subcategory
    const allFlat = categories.flatMap(c => [c, ...(c.subcategories || [])]);
    const txnCat = allFlat.find(c => c.id === t.category_id);
    const isSubcat = txnCat?.parent_id;
    setForm({
      type: t.type,
      category_id: isSubcat ? String(txnCat.parent_id) : String(t.category_id || ''),
      subcategory_id: isSubcat ? String(t.category_id) : '',
      amount: t.amount,
      description: t.description || '',
      date: t.date
    });
    setEditId(t.id); setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, category_id: form.category_id };
    try {
      if (editId) {
        await api.put(`/transactions/${editId}`, payload);
        toast.success('Movimiento actualizado');
      } else {
        await api.post('/transactions', payload);
        toast.success('Movimiento registrado');
      }
      setShowModal(false); load();
      // Verificar alertas de presupuesto
      checkBudgetAlerts(month, year);
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este movimiento?')) return;
    try { await api.delete(`/transactions/${id}`); toast.success('Eliminado'); load(); }
    catch (err) { toast.error(err.message); }
  };

  const filtered = filterType === 'all' ? transactions : transactions.filter(t => t.type === filterType);
  const totalIncome = transactions.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
  const totalExpense = transactions.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);

  return (
    <div style={styles.page} className="animate-fadeIn">
      <div style={styles.topRow}>
        <div style={styles.periodRow}>
          <select className="form-select" value={month} onChange={e => setMonth(Number(e.target.value))} style={styles.sel}>
            {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
          </select>
          <select className="form-select" value={year} onChange={e => setYear(Number(e.target.value))} style={styles.sel}>
            {[2023,2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <button className="btn btn-primary" onClick={openAdd} style={{ flexShrink: 0 }}>+ Nuevo</button>
      </div>

    {/* Summary mini cards */}
      <div style={styles.miniCards}>
        <div style={{ ...styles.miniCard, borderColor: 'rgba(0,212,170,0.2)' }}>
          <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>Ingresos</p>
          <p style={{ fontWeight: 700, color: 'var(--green)', fontSize: '0.88rem', marginTop:4 }}>
            {hiddenAmounts ? '••••' : `+${formatCurrency(totalIncome, currency)}`}
          </p>
        </div>
        <div style={{ ...styles.miniCard, borderColor: 'rgba(255,84,112,0.2)' }}>
          <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>Gastos</p>
          <p style={{ fontWeight: 700, color: 'var(--red)', fontSize: '0.88rem', marginTop:4 }}>
            {hiddenAmounts ? '••••' : `-${formatCurrency(totalExpense, currency)}`}
          </p>
        </div>
        <div style={{ ...styles.miniCard, borderColor: 'rgba(124,111,255,0.2)' }}>
          <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>Balance</p>
          <p style={{ fontWeight: 700, color: totalIncome-totalExpense >= 0 ? 'var(--green)' : 'var(--red)', fontSize: '0.88rem', marginTop:4 }}>
            {hiddenAmounts ? '••••' : formatCurrency(totalIncome - totalExpense, currency)}
          </p>
        </div>
      </div>

      {/* Ojo separado debajo de las cards */}
      <div style={{ display:'flex', justifyContent:'flex-end' }}>
        <button onClick={() => setHiddenAmounts(h => {
          localStorage.setItem('hideTransactions', String(!h));
          return !h;
        })} style={{ ...styles.eyeBtnSm, display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:99, background:'var(--bg-card)', border:'1px solid var(--border)' }}>
          <span>{hiddenAmounts ? '🙈' : '👁️'}</span>
          <span style={{ fontSize:'0.78rem', color:'var(--text-secondary)' }}>
            {hiddenAmounts ? 'Mostrar montos' : 'Ocultar montos'}
          </span>
        </button>
      </div>

      {/* Filter pills */}
      <div style={styles.pills}>
        {[['all','Todos'],['income','Ingresos'],['expense','Gastos']].map(([v,l]) => (
          <button key={v} onClick={() => setFilterType(v)}
            style={{ ...styles.pill, ...(filterType === v ? styles.pillActive : {}) }}>
            {l}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:40 }}>
          <div className="spinner" style={{ width:32, height:32 }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={styles.empty}>
          <span style={{ fontSize: 44 }}>📭</span>
          <p>No hay movimientos</p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {filtered.map(t => (
            <div key={t.id} className="card card-sm" style={styles.txnRow}>
              <div style={{
                width:44, height:44, borderRadius:12, flexShrink:0,
                background: t.category_color ? `${t.category_color}20` : 'var(--bg-input)',
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:20,
              }}>
                {t.category_icon || (t.type==='income'?'💰':'💸')}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ fontWeight:500, fontSize:'0.9rem' }} className="truncate">
                  {t.description || t.category_name || 'Sin descripción'}
                </p>
                <p style={{ fontSize:'0.75rem', color:'var(--text-secondary)', marginTop:2 }}>
                  {t.category_name || '—'} · {new Date(t.date+'T00:00:00').toLocaleDateString('es-CO',{day:'2-digit',month:'short'})}
                </p>
              </div>
              <span style={{ fontWeight:700, color: t.type==='income'?'var(--green)':'var(--red)', flexShrink:0, fontSize:'0.95rem' }}>
                {hiddenAmounts ? '••••' : `${t.type==='income'?'+':'-'}${formatCurrency(t.amount, currency)}`}
              </span>
              <div style={{ display:'flex', gap:4, flexShrink:0 }}>
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => openEdit(t)} title="Editar">✏️</button>
                <button className="btn btn-danger btn-icon btn-sm" onClick={() => handleDelete(t.id)} title="Eliminar">🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">{editId ? 'Editar' : 'Nuevo'} movimiento</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave} style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {/* Type toggle */}
              <div style={styles.typeToggle}>
                {[['income','↑ Ingreso'],['expense','↓ Gasto']].map(([v,l]) => (
                  <button key={v} type="button"
                    onClick={() => setForm({...form, type:v, category_id:'', subcategory_id:''})}
                    style={{
                      ...styles.typeBtn,
                      ...(form.type===v ? (v==='income' ? styles.typeBtnIncome : styles.typeBtnExpense) : {}),
                    }}>
                    {l}
                  </button>
                ))}
              </div>

             <div className="form-group">
                <label className="form-label">Monto</label>
                <div
                  onClick={() => setShowKeypad(true)}
                  style={styles.amountDisplay}
                >
                  <span style={{
                    fontSize: form.amount ? '1.6rem' : '1rem',
                    fontWeight: 800,
                    fontFamily: 'var(--font-display)',
                    color: form.amount ? 'var(--text-primary)' : 'var(--text-muted)',
                  }}>
                    {form.amount ? formatCurrency(parseFloat(form.amount), currency) : 'Toca para ingresar monto'}
                  </span>
                </div>
                {showKeypad && (
                  <NumericKeypad
                    value={form.amount}
                    onChange={(val) => setForm({...form, amount: val})}
                    onConfirm={() => setShowKeypad(false)}
                    currency={currency}
                    label="Monto del movimiento"
                  />
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Categoría</label>
                <select className="form-select" value={form.category_id}
                  onChange={e => setForm({...form, category_id:e.target.value})}>
                  <option value="">Sin categoría</option>
                  {categories
                    .filter(c => c.type === form.type)
                    .flatMap(parent => [
                      <option key={parent.id} value={parent.id}>{parent.icon} {parent.name}</option>,
                      ...(parent.subcategories || []).map(sub =>
                        <option key={sub.id} value={sub.id}>  └ {sub.icon} {sub.name}</option>
                      )
                    ])
                  }
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Descripción</label>
                <input type="text" className="form-input" placeholder="Descripción opcional"
                  value={form.description} onChange={e => setForm({...form, description:e.target.value})} />
              </div>

              <div className="form-group">
                <label className="form-label">Fecha</label>
                <input type="date" className="form-input" value={form.date}
                  onChange={e => setForm({...form, date:e.target.value})} required />
              </div>

              <div style={{ display:'flex', gap:10, marginTop:4 }}>
                <button type="button" className="btn btn-secondary" style={{ flex:1 }} onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ flex:2 }} disabled={saving}>
                  {saving ? <span className="spinner" /> : (editId ? 'Guardar cambios' : 'Registrar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { display:'flex', flexDirection:'column', gap:20 },
  topRow: { display:'flex', gap:10, alignItems:'center' },
  periodRow: { display:'flex', gap:8, flex:1 },
  sel: { flex:1, padding:'10px 12px', fontSize:'0.85rem' },
  miniCards: { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 },
  miniCard: { background:'var(--bg-card)', border:'1px solid', borderRadius:'var(--radius-sm)', padding:'14px 12px' },
  pills: { display:'flex', gap:8 },
  pill: { padding:'8px 16px', borderRadius:99, border:'1px solid var(--border)', background:'transparent', color:'var(--text-secondary)', cursor:'pointer', fontSize:'0.83rem', fontFamily:'var(--font-body)', transition:'all 0.2s' },
  pillActive: { background:'var(--bg-card-hover)', color:'var(--text-primary)', borderColor:'var(--accent)' },
  txnRow: { display:'flex', alignItems:'center', gap:12 },
  empty: { display:'flex', flexDirection:'column', alignItems:'center', gap:12, padding:'40px 20px', color:'var(--text-secondary)', textAlign:'center' },
  typeToggle: { display:'flex', gap:8, background:'var(--bg-input)', borderRadius:'var(--radius-sm)', padding:4 },
  typeBtn: { flex:1, padding:'10px', border:'none', borderRadius:8, cursor:'pointer', fontFamily:'var(--font-body)', fontSize:'0.9rem', fontWeight:600, background:'transparent', color:'var(--text-muted)', transition:'all 0.2s' },
  typeBtnIncome: { background:'var(--green-dim)', color:'var(--green)' },
  typeBtnExpense: { background:'var(--red-dim)', color:'var(--red)' },
  eyeBtnSm: { background:'transparent', border:'none', cursor:'pointer', padding:'2px 4px', borderRadius:6, fontSize:13, lineHeight:1 },
  amountDisplay: {
  background: 'var(--bg-input)',
  border: '1.5px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: '16px',
  cursor: 'pointer',
  minHeight: 58,
  display: 'flex',
  alignItems: 'center',
  transition: 'border-color 0.2s',
},
};
